/**
 * sessionStore.ts
 * ───────────────
 * Single source of truth for auth state. Module-level (not React),
 * so fetchWithAuth and non-component code can read it synchronously.
 *
 * Priority order (set at bootstrap):
 *   1. device   — localStorage device token authenticated via /api/devices/auth
 *   2. server   — server JWT issued from /api/auth/session (Auth0 exchange or refresh cookie)
 *   3. none     — unauthenticated
 */

export type AuthMode = "device" | "server" | "none";

export interface SessionUser {
  id: string;
  email?: string;
  name?: string;
  sub?: string;
  /** Profile id for care-recipient device sessions */
  profileId?: string | null;
  /** Human-readable device name (device mode only) */
  deviceName?: string;
  account_role?: string;
  subscription?: string;
  /** Derived or Auth0-sourced roles, e.g. ["Admin", "Lite"] */
  roles: string[];
  /** Auth0-sourced or empty for device/cookie sessions */
  permissions: string[];
}

export interface SessionState {
  mode: AuthMode;
  user: SessionUser | null;
  /** Short-lived server access token (HS256, in-memory only) */
  accessToken: string | null;
  /** True once the bootstrap sequence has completed */
  initialized: boolean;
}

const EXPLICIT_LOGOUT_HANDOFF_KEY = "showme_explicit_logout_handoff_v1";

export function setExplicitLogoutHandoff(): void {
  try {
    sessionStorage.setItem(EXPLICIT_LOGOUT_HANDOFF_KEY, "1");
  } catch {
    // Logout must continue even if browser storage is unavailable.
  }
}

export function hasExplicitLogoutHandoff(): boolean {
  try {
    return sessionStorage.getItem(EXPLICIT_LOGOUT_HANDOFF_KEY) === "1";
  } catch {
    return false;
  }
}

export function consumeExplicitLogoutHandoff(): boolean {
  if (!hasExplicitLogoutHandoff()) return false;

  try {
    sessionStorage.removeItem(EXPLICIT_LOGOUT_HANDOFF_KEY);
  } catch {
    // The handoff still counts as consumed for this bootstrap attempt.
  }
  return true;
}

let _state: SessionState = {
  mode: "none",
  user: null,
  accessToken: null,
  initialized: false,
};

type Listener = () => void;
let _listeners: Listener[] = [];

export function getSession(): SessionState {
  return _state;
}

export function setSession(partial: Partial<SessionState>): void {
  _state = { ..._state, ...partial };
  _listeners.forEach((fn) => fn());
}

/** Subscribe to session changes. Returns unsubscribe fn. */
export function onSessionChange(fn: Listener): () => void {
  _listeners.push(fn);
  return () => {
    _listeners = _listeners.filter((l) => l !== fn);
  };
}

// ── Convenience accessors (backward-compat with serverTokenStore) ──

export function getServerAccessToken(): string | null {
  return _state.accessToken;
}

export function setServerAccessToken(token: string | null): void {
  setSession({ accessToken: token });
}

export function clearServerAccessToken(): void {
  setSession({ accessToken: null });
}

export function isDeviceMode(): boolean {
  return _state.mode === "device";
}

export function getDeviceModeUser(): SessionUser | null {
  return _state.mode === "device" ? _state.user : null;
}

export function setDeviceMode(user: Record<string, any> | null): void {
  if (user) {
    setSession({ mode: "device", user: user as SessionUser });
  } else {
    setSession({ mode: "none", user: null, accessToken: null });
  }
}

/** Derive roles from DB user fields (used when Auth0 payload isn't available) */
export function deriveRoles(
  accountRole?: string,
  subscription?: string,
  plan?: string
): string[] {
  const roles: string[] = [];
  if (accountRole === "admin") roles.push("Admin");
  if (accountRole === "tester") roles.push("Tester");
  const tier = (subscription || plan || "free").toLowerCase();
  if (tier.includes("premium")) roles.push("Premium");
  else if (tier.includes("plus")) roles.push("Plus");
  else roles.push("Lite");
  return roles;
}
